// Phase 2 P2-CG — Cost Dashboard.
//
// Founder/admin view of the cost governance framework. Shows the daily
// spend meter against the $5 pilot ceiling, last-14-day burn, per-function
// rollup, model usage breakdown, fallback activation rate, throttled-call
// counts, and the current circuit-breaker state.
//
// Per the Cost Governance Framework v1.0 § 08 — Commander Dashboard.

import type { CSSProperties } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";
import {
  useCostDashboard,
  type DailySpendRow,
  type SpendByFunctionRow,
} from "../../cost/useCostDashboard.ts";

// ─── Styles ───────────────────────────────────────────────────────

const pageRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const pageHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  flexWrap: "wrap",
};

const pageTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  margin: 0,
};

const pageSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: 4,
};

const eyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const sectionEyebrow: CSSProperties = {
  ...eyebrow,
  margin: "var(--sz-space-3) 0 var(--sz-space-2)",
};

const card: CSSProperties = {
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md, 8px)",
  padding: "var(--sz-space-3)",
};

const heroRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.6fr 1fr 1fr",
  gap: "var(--sz-space-3)",
};

const headlineMetric: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(2rem, 5vw, 3.4rem)",
  color: "var(--sz-text-primary)",
  letterSpacing: "1px",
  lineHeight: 1,
  marginTop: 6,
};

const meter: CSSProperties = {
  position: "relative",
  height: 8,
  background: "var(--sz-bg-surface-3)",
  borderRadius: 4,
  overflow: "hidden",
  marginTop: 12,
};

const meterFill = (pct: number, status: "ok" | "warning" | "breaker"): CSSProperties => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: `${Math.min(100, pct * 100)}%`,
  background:
    status === "breaker"
      ? "var(--sz-danger)"
      : status === "warning"
        ? "var(--sz-warning)"
        : "var(--sz-amber)",
  transition: "width 200ms ease",
});

const tableRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 140px 80px 90px 110px",
  gap: "var(--sz-space-2)",
  padding: "8px 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "0.8rem",
  alignItems: "baseline",
};

const sparkRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "100px 1fr 90px 70px 70px",
  gap: "var(--sz-space-2)",
  padding: "8px 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "0.8rem",
  alignItems: "baseline",
};

// ─── Helpers ──────────────────────────────────────────────────────

const fmtUsd = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n)
    ? "—"
    : n < 0.01
      ? `$${n.toFixed(4)}`
      : `$${n.toFixed(2)}`;

const fmtInt = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : n.toLocaleString();

const fmtPct = (n: number | null | undefined, digits = 1): string =>
  n === null || n === undefined || !Number.isFinite(n) ? "—" : `${(n * 100).toFixed(digits)}%`;

const fmtDate = (iso: string): string => {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Aggregate across all rows of agent_spend_by_function (last 30d)
function aggregateByFunction(rows: SpendByFunctionRow[]): {
  fn: string;
  cost: number;
  calls: number;
  models: string[];
}[] {
  const map = new Map<string, { fn: string; cost: number; calls: number; models: Set<string> }>();
  for (const r of rows) {
    const k = r.function_name;
    const ex = map.get(k);
    const cost = Number(r.cost_usd ?? 0);
    if (ex) {
      ex.cost += cost;
      ex.calls += r.calls;
      ex.models.add(r.model_used);
    } else {
      map.set(k, { fn: k, cost, calls: r.calls, models: new Set([r.model_used]) });
    }
  }
  return Array.from(map.values())
    .map((v) => ({ fn: v.fn, cost: v.cost, calls: v.calls, models: Array.from(v.models) }))
    .sort((a, b) => b.cost - a.cost);
}

function aggregateByModel(rows: SpendByFunctionRow[]): { model: string; cost: number; calls: number }[] {
  const map = new Map<string, { cost: number; calls: number }>();
  for (const r of rows) {
    const ex = map.get(r.model_used) ?? { cost: 0, calls: 0 };
    ex.cost += Number(r.cost_usd ?? 0);
    ex.calls += r.calls;
    map.set(r.model_used, ex);
  }
  return Array.from(map.entries())
    .map(([model, v]) => ({ model, cost: v.cost, calls: v.calls }))
    .sort((a, b) => b.cost - a.cost);
}

// ─── Page ─────────────────────────────────────────────────────────

export function CostDashboardPage(): JSX.Element {
  const { dailyHistory, byFunction, config, breaker, loading, error } = useCostDashboard();

  if (loading) {
    return (
      <div style={pageRoot}>
        <div style={pageHeader}>
          <div>
            <h1 style={pageTitle}>Cost Governance</h1>
            <div style={pageSubtitle}>Commander Dashboard · Framework v1.0</div>
          </div>
        </div>
        <AwaitingDataPanel
          eyebrow="Cost"
          title="Loading…"
          body="Fetching spend tracker, governance config, and breaker state."
        />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div style={pageRoot}>
        <div style={pageHeader}>
          <div>
            <h1 style={pageTitle}>Cost Governance</h1>
          </div>
        </div>
        <AwaitingDataPanel eyebrow="Cost" title="Read failed." body={error} />
      </div>
    );
  }

  const todayRow = dailyHistory[0];
  const todaySpent = Number(todayRow?.total_usd ?? 0);
  const ceiling = Number(config?.daily_ceiling_usd ?? 5);
  const warningThresh = Number(config?.warning_threshold_pct ?? 0.8);
  const pct = breaker?.pct ?? (ceiling > 0 ? todaySpent / ceiling : 0);
  const meterStatus: "ok" | "warning" | "breaker" =
    pct >= 1 ? "breaker" : pct >= warningThresh ? "warning" : "ok";

  const fnAgg = aggregateByFunction(byFunction);
  const modelAgg = aggregateByModel(byFunction);

  const totalLast14 = dailyHistory.reduce((s, r) => s + Number(r.total_usd ?? 0), 0);
  const totalFallback14 = dailyHistory.reduce((s, r) => s + Number(r.fallback_calls ?? 0), 0);
  const totalCalls14 = dailyHistory.reduce((s, r) => s + Number(r.total_calls ?? 0), 0);
  const totalThrottled14 = dailyHistory.reduce((s, r) => s + Number(r.throttled_calls ?? 0), 0);
  const totalBlocked14 = dailyHistory.reduce((s, r) => s + Number(r.blocked_calls ?? 0), 0);
  const fallbackRate = totalCalls14 > 0 ? totalFallback14 / totalCalls14 : 0;

  // Project month-end spend if today's pace continues
  const dailyAvgLast14 = dailyHistory.length > 0 ? totalLast14 / dailyHistory.length : 0;
  const projectedMonthly = dailyAvgLast14 * 30;

  return (
    <div style={pageRoot}>
      <div style={pageHeader}>
        <div>
          <h1 style={pageTitle}>Cost Governance</h1>
          <div style={pageSubtitle}>Commander Dashboard · Framework v1.0</div>
        </div>
        <span
          style={{
            fontFamily: "var(--sz-font-mono)",
            fontSize: "0.75rem",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            padding: "4px 12px",
            borderRadius: 3,
            border: "1px solid",
            background:
              meterStatus === "breaker"
                ? "var(--sz-danger-dim)"
                : meterStatus === "warning"
                  ? "var(--sz-warning-dim)"
                  : "var(--sz-success-dim)",
            color:
              meterStatus === "breaker"
                ? "var(--sz-danger-text)"
                : meterStatus === "warning"
                  ? "var(--sz-warning-text)"
                  : "var(--sz-success-text)",
            borderColor:
              meterStatus === "breaker"
                ? "var(--sz-danger)"
                : meterStatus === "warning"
                  ? "var(--sz-warning)"
                  : "var(--sz-success)",
          }}
        >
          {meterStatus === "breaker"
            ? "Circuit breaker active"
            : meterStatus === "warning"
              ? "Warning threshold"
              : "Operating normal"}
        </span>
      </div>

      {/* Hero row */}
      <div style={heroRow}>
        {/* Today's spend with meter */}
        <article style={card}>
          <div style={eyebrow}>Today's spend</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={headlineMetric}>{fmtUsd(todaySpent)}</span>
            <span
              style={{
                fontFamily: "var(--sz-font-mono)",
                fontSize: "0.85rem",
                color: "var(--sz-text-muted)",
              }}
            >
              of {fmtUsd(ceiling)} ({fmtPct(pct, 2)})
            </span>
          </div>
          <div style={meter}>
            <div style={meterFill(pct, meterStatus)} />
          </div>
          <div
            style={{
              ...eyebrow,
              marginTop: 8,
              fontSize: "0.7rem",
              letterSpacing: "1px",
            }}
          >
            {todayRow?.total_calls ?? 0} calls · {todayRow?.fallback_calls ?? 0} fallback ·{" "}
            {todayRow?.throttled_calls ?? 0} throttled · {todayRow?.blocked_calls ?? 0} blocked
          </div>
        </article>

        {/* 14d burn */}
        <article style={card}>
          <div style={eyebrow}>Last 14 days</div>
          <div style={{ ...headlineMetric, fontSize: "clamp(1.4rem, 4vw, 2.4rem)" }}>
            {fmtUsd(totalLast14)}
          </div>
          <div style={{ ...eyebrow, marginTop: 8 }}>{fmtInt(totalCalls14)} calls</div>
          <div style={{ ...eyebrow, marginTop: 4 }}>
            avg {fmtUsd(dailyAvgLast14)}/day
          </div>
        </article>

        {/* Projected month */}
        <article style={card}>
          <div style={eyebrow}>Projected month-end</div>
          <div
            style={{
              ...headlineMetric,
              fontSize: "clamp(1.4rem, 4vw, 2.4rem)",
              color:
                projectedMonthly > 150
                  ? "var(--sz-warning-text)"
                  : "var(--sz-text-primary)",
            }}
          >
            {fmtUsd(projectedMonthly)}
          </div>
          <div style={{ ...eyebrow, marginTop: 8 }}>
            cap: ${(config?.monthly_cap_anthropic ?? 120) + (config?.monthly_cap_openai ?? 60)} combined
          </div>
        </article>
      </div>

      {/* Cost by function */}
      <div style={sectionEyebrow}>Cost by function (30d)</div>
      <article style={card}>
        <div
          style={{
            ...tableRow,
            borderBottom: "1px solid var(--sz-border-default)",
            color: "var(--sz-text-muted)",
            fontSize: "0.7rem",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          <span>Function</span>
          <span>Models</span>
          <span style={{ textAlign: "right" }}>Calls</span>
          <span style={{ textAlign: "right" }}>Total</span>
          <span style={{ textAlign: "right" }}>Avg/call</span>
        </div>
        {fnAgg.length === 0 ? (
          <div
            style={{
              fontFamily: "var(--sz-font-mono)",
              fontSize: "0.85rem",
              color: "var(--sz-text-muted)",
              padding: 12,
            }}
          >
            No spend recorded in the last 30 days.
          </div>
        ) : (
          fnAgg.map((row) => (
            <div key={row.fn} style={tableRow}>
              <span style={{ color: "var(--sz-text-primary)" }}>{row.fn}</span>
              <span style={{ color: "var(--sz-text-secondary)", fontSize: "0.7rem" }}>
                {row.models.join(", ")}
              </span>
              <span style={{ textAlign: "right" }}>{fmtInt(row.calls)}</span>
              <span style={{ textAlign: "right", color: "var(--sz-amber)" }}>
                {fmtUsd(row.cost)}
              </span>
              <span
                style={{
                  textAlign: "right",
                  color: "var(--sz-text-secondary)",
                  fontSize: "0.75rem",
                }}
              >
                {row.calls > 0 ? fmtUsd(row.cost / row.calls) : "—"}
              </span>
            </div>
          ))
        )}
      </article>

      {/* Model usage breakdown */}
      <div style={sectionEyebrow}>Model usage (30d)</div>
      <article style={card}>
        <div
          style={{
            ...tableRow,
            gridTemplateColumns: "1fr 80px 100px 100px 110px",
            borderBottom: "1px solid var(--sz-border-default)",
            color: "var(--sz-text-muted)",
            fontSize: "0.7rem",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          <span>Model</span>
          <span style={{ textAlign: "right" }}>Calls</span>
          <span style={{ textAlign: "right" }}>Share</span>
          <span style={{ textAlign: "right" }}>Total</span>
          <span style={{ textAlign: "right" }}>Avg/call</span>
        </div>
        {modelAgg.length === 0 ? (
          <div
            style={{
              fontFamily: "var(--sz-font-mono)",
              fontSize: "0.85rem",
              color: "var(--sz-text-muted)",
              padding: 12,
            }}
          >
            No model spend recorded.
          </div>
        ) : (
          modelAgg.map((row) => {
            const totalCalls = modelAgg.reduce((s, r) => s + r.calls, 0);
            const share = totalCalls > 0 ? row.calls / totalCalls : 0;
            const isExpensive = row.model.startsWith("gpt-4o") && !row.model.includes("mini");
            const flag = isExpensive && share > 0.1;
            return (
              <div
                key={row.model}
                style={{
                  ...tableRow,
                  gridTemplateColumns: "1fr 80px 100px 100px 110px",
                }}
              >
                <span style={{ color: "var(--sz-text-primary)" }}>
                  {row.model}
                  {flag && (
                    <span
                      style={{
                        marginLeft: 8,
                        color: "var(--sz-warning-text)",
                        fontSize: "0.7rem",
                      }}
                    >
                      ⚠ &gt;10%
                    </span>
                  )}
                </span>
                <span style={{ textAlign: "right" }}>{fmtInt(row.calls)}</span>
                <span style={{ textAlign: "right", color: "var(--sz-text-secondary)" }}>
                  {fmtPct(share)}
                </span>
                <span style={{ textAlign: "right", color: "var(--sz-amber)" }}>
                  {fmtUsd(row.cost)}
                </span>
                <span style={{ textAlign: "right", color: "var(--sz-text-secondary)" }}>
                  {row.calls > 0 ? fmtUsd(row.cost / row.calls) : "—"}
                </span>
              </div>
            );
          })
        )}
      </article>

      {/* Daily history */}
      <div style={sectionEyebrow}>Last 14 days</div>
      <article style={card}>
        <div
          style={{
            ...sparkRow,
            borderBottom: "1px solid var(--sz-border-default)",
            color: "var(--sz-text-muted)",
            fontSize: "0.7rem",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          <span>Date</span>
          <span>Bar</span>
          <span style={{ textAlign: "right" }}>Spend</span>
          <span style={{ textAlign: "right" }}>Calls</span>
          <span style={{ textAlign: "right" }}>Throttled</span>
        </div>
        {dailyHistory.length === 0 ? (
          <div
            style={{
              fontFamily: "var(--sz-font-mono)",
              fontSize: "0.85rem",
              color: "var(--sz-text-muted)",
              padding: 12,
            }}
          >
            No daily spend rows recorded.
          </div>
        ) : (
          dailyHistory.map((row: DailySpendRow) => {
            const dayPct = ceiling > 0 ? Number(row.total_usd ?? 0) / ceiling : 0;
            const dayStatus: "ok" | "warning" | "breaker" =
              dayPct >= 1 ? "breaker" : dayPct >= warningThresh ? "warning" : "ok";
            return (
              <div key={row.date} style={sparkRow}>
                <span style={{ color: "var(--sz-text-secondary)" }}>{fmtDate(row.date)}</span>
                <div style={{ ...meter, marginTop: 0 }}>
                  <div style={meterFill(Math.min(1, dayPct), dayStatus)} />
                </div>
                <span style={{ textAlign: "right", color: "var(--sz-amber)" }}>
                  {fmtUsd(row.total_usd)}
                </span>
                <span style={{ textAlign: "right" }}>{fmtInt(row.total_calls)}</span>
                <span
                  style={{
                    textAlign: "right",
                    color: (row.throttled_calls ?? 0) > 0 ? "var(--sz-success-text)" : "var(--sz-text-muted)",
                  }}
                >
                  {fmtInt(row.throttled_calls)}
                </span>
              </div>
            );
          })
        )}
      </article>

      {/* Governance health metrics */}
      <div style={sectionEyebrow}>Governance health</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "var(--sz-space-3)",
        }}
      >
        <article style={card}>
          <div style={eyebrow}>Throttled calls 14d</div>
          <div
            style={{
              ...headlineMetric,
              fontSize: "clamp(1.4rem, 3vw, 1.8rem)",
              color: "var(--sz-success-text)",
            }}
          >
            {fmtInt(totalThrottled14)}
          </div>
          <div style={{ ...eyebrow, marginTop: 4, fontSize: "0.7rem" }}>
            throttle gate working
          </div>
        </article>
        <article style={card}>
          <div style={eyebrow}>Breaker blocks 14d</div>
          <div
            style={{
              ...headlineMetric,
              fontSize: "clamp(1.4rem, 3vw, 1.8rem)",
              color: totalBlocked14 > 0 ? "var(--sz-warning-text)" : "var(--sz-text-primary)",
            }}
          >
            {fmtInt(totalBlocked14)}
          </div>
          <div style={{ ...eyebrow, marginTop: 4, fontSize: "0.7rem" }}>
            ceiling enforced
          </div>
        </article>
        <article style={card}>
          <div style={eyebrow}>Fallback rate 14d</div>
          <div
            style={{
              ...headlineMetric,
              fontSize: "clamp(1.4rem, 3vw, 1.8rem)",
              color:
                fallbackRate > (config?.fallback_alert_pct ?? 0.15)
                  ? "var(--sz-warning-text)"
                  : "var(--sz-text-primary)",
            }}
          >
            {fmtPct(fallbackRate, 2)}
          </div>
          <div style={{ ...eyebrow, marginTop: 4, fontSize: "0.7rem" }}>
            alert &gt; {fmtPct(config?.fallback_alert_pct ?? 0.15)}
          </div>
        </article>
        <article style={card}>
          <div style={eyebrow}>Phase</div>
          <div
            style={{
              ...headlineMetric,
              fontSize: "clamp(1.2rem, 3vw, 1.6rem)",
              color: "var(--sz-amber)",
              textTransform: "uppercase",
              letterSpacing: "2px",
            }}
          >
            {config?.pilot_phase ? "Pilot" : "Scale"}
          </div>
          <div style={{ ...eyebrow, marginTop: 4, fontSize: "0.7rem" }}>
            ceiling {fmtUsd(ceiling)}/day
          </div>
        </article>
      </div>

      {/* Honesty footer */}
      <div
        style={{
          fontFamily: "var(--sz-font-mono)",
          fontSize: "0.7rem",
          color: "var(--sz-text-muted)",
          letterSpacing: "1px",
          textTransform: "uppercase",
          textAlign: "center",
          marginTop: "var(--sz-space-2)",
        }}
      >
        <Icon name="info" size={12} /> Cost Governance Framework v1.0 · Pre-flight breaker on
        every API call · Cheap-first model probe · Daily ceiling enforced at the DB layer
      </div>
    </div>
  );
}
